import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Placeholder posts if none exist
  const displayPosts = posts.length > 0 ? posts : [
    {
      id: '1',
      title: t('blog.sample1Title', 'The Joy of Serving Others'),
      excerpt: t('blog.sample1Excerpt', 'Discover how serving in our community brings fulfillment and purpose to your life while helping those in need.'),
      author: 'Pastor Liza',
      created_at: '2024-12-01',
      image: 'https://images.unsplash.com/photo-1725596854669-61fb4badaf4d?w=600'
    },
    {
      id: '2',
      title: t('blog.sample2Title', 'Food Pantry Impact: 500 Families Served'),
      excerpt: t('blog.sample2Excerpt', 'A look back at our food pantry ministry and the incredible impact it has had on families in Kissimmee.'),
      author: 'Ministry Team',
      created_at: '2024-11-15',
      image: 'https://images.unsplash.com/photo-1599659593072-10de2e109496?w=600'
    },
    {
      id: '3',
      title: t('blog.sample3Title', 'Growing in Faith Together'),
      excerpt: t('blog.sample3Excerpt', 'How our Bible study groups are helping members deepen their understanding of Scripture and build lasting friendships.'),
      author: 'Co-Pastor Bernardo',
      created_at: '2024-11-01',
      image: 'https://images.unsplash.com/photo-1655392032737-6a4fabe2f15b?w=600'
    }
  ];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="blog-page">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('blog.storiesUpdates', 'Stories & Updates')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('blog.title', 'News & Blog')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              {t('blog.subtitle', 'Stories of faith, updates from our ministry, and reflections on serving our community.')}
            </p>
          </motion.div>

          {/* Featured Post */}
          {displayPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              <div className="bg-white rounded-3xl overflow-hidden border border-gold/10 hover:border-gold/30 transition-all duration-300 md:flex">
                <div className="md:w-1/2 aspect-video md:aspect-auto">
                  <img 
                    src={displayPosts[0].image || 'https://images.unsplash.com/photo-1655392032737-6a4fabe2f15b?w=800'} 
                    alt={displayPosts[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{t('blog.featured', 'Featured')}</span>
                  <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-4">{displayPosts[0].title}</h2>
                  <p className="text-charcoal/70 mb-6">{displayPosts[0].excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-charcoal/50 mb-6">
                    <span className="flex items-center gap-1">
                      <User size={14} /> {displayPosts[0].author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {formatDate(displayPosts[0].created_at)}
                    </span>
                  </div>
                  <Link 
                    to={`/blog/${displayPosts[0].id}`}
                    className="inline-flex items-center gap-2 text-burgundy hover:text-purple transition-colors"
                  >
                    {t('blog.readMore', 'Read More')} <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayPosts.slice(1).map((post, i) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden border border-gold/10 hover:border-gold/30 transition-all duration-300 hover:-translate-y-1"
                data-testid={`blog-card-${i}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={post.image || 'https://images.unsplash.com/photo-1655392032737-6a4fabe2f15b?w=600'} 
                    alt={post.title}
                    className="w-full h-full object-cover transform transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-charcoal/50 mb-3">
                    <span>{post.author}</span>
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                  <h3 className="font-serif text-lg text-charcoal mb-2 line-clamp-2">{post.title}</h3>
                  <p className="text-charcoal/60 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <Link 
                    to={`/blog/${post.id}`}
                    className="text-sm text-burgundy hover:text-purple transition-colors inline-flex items-center gap-1"
                  >
                    {t('blog.readMore', 'Read More')} <ChevronRight size={14} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>

          {displayPosts.length === 0 && !loading && (
            <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
              <p className="text-charcoal/50">{t('blog.noPosts', 'No blog posts yet. Check back soon!')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
